import { localesWithTempClosedTranslation } from '@/constants/supportedLocales';
import * as timeHelper from './dateTimeHelper';

export function getLoungeOpenTimesForToday(locale, lounge) {
  const currentDay = timeHelper.getCurrentDayForTimeZone(
    /*
      our API returns a map whose keys are day names
      written in english so the "currentDay" of the week needs
      to be in english to find the hours in the data
    */
    'en-us',
    lounge.airport.time_zone
  );

  return lounge?.hours?.[currentDay]?.[0];
}

export function getAllLoungeOpenTimesForToday({ lounge }) {
  /*
      our API returns a map whose keys are day names
      written in english so the "currentDay" of the week needs
      to be in english to find the hours in the data
  */
  const currentDay = timeHelper.getCurrentDayForTimeZone(
    'en-us',
    lounge.airport.time_zone
  );

  return lounge?.hours?.[currentDay];
}

export function isLoungeTempClosed(lounge) {
  return lounge.name.toLocaleLowerCase().includes('temporarily closed');
}

export function isLoungeOpen(locale, lounge) {
  const loungeTimeZone = lounge?.airport?.time_zone || '';
  let loungeLocalTime = timeHelper.getCurrentTimeForTimeZone(
    locale,
    loungeTimeZone
  );

  const loungeTimes = getAllLoungeOpenTimesForToday({ lounge });

  if (isLoungeTempClosed(lounge)) {
    // Lounges close temporarily for maintenance or other events.
    // Design/Product team want to show closed text rather than nothing.
    // There isn't a "temporarily closed" property, they just updated the lounge name and sometimes the hours.
    return false;
  }

  return loungeTimes?.some(({ open, close }) => {
    const openLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
      open,
      loungeTimeZone
    );
    let closeLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
      close,
      loungeTimeZone
    );

    if (!(openLoungeTime.isValid && closeLoungeTime.isValid)) {
      return null;
    }

    if (closeLoungeTime <= openLoungeTime) {
      closeLoungeTime = closeLoungeTime.plus({ days: 1 });
    }
    /* If the time goes to the next day, the object for loungeTimes is for the next day,
  so the object for the local time needs to be updated */
    if (timeHelper.isUsingNextDayTimes(loungeLocalTime, openLoungeTime)) {
      loungeLocalTime = loungeLocalTime.plus({ days: 1 });
    }

    return timeHelper.isBetweenTimes(
      loungeLocalTime,
      openLoungeTime,
      closeLoungeTime
    );
  });
}

export function getLoungeCurrentStatus(locale, lounge, format) {
  const loungeTimeZone = lounge?.airport?.time_zone || '';
  let loungeLocalTime = timeHelper.getCurrentTimeForTimeZone(
    locale,
    loungeTimeZone
  );

  const loungeTimes = getAllLoungeOpenTimesForToday({ lounge }) || [];

  if (isLoungeTempClosed(lounge)) {
    return {
      isOpen: false,
      openingTime: '',
      closingTime: '',
    };
  }

  // eslint-disable-next-line no-restricted-syntax -- Need to return from function in between the iteration
  for (const loungeTime of loungeTimes) {
    const openLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
      loungeTime.open,
      loungeTimeZone
    );
    let closeLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
      loungeTime.close,
      loungeTimeZone
    );

    if (!openLoungeTime.isValid || !closeLoungeTime.isValid) {
      return {
        isOpen: false,
        openingTime: '',
        closingTime: '',
      };
    }
    if (closeLoungeTime <= openLoungeTime) {
      closeLoungeTime = closeLoungeTime.plus({ days: 1 });
    }
    /* If the time goes to the next day, the object for loungeTimes is for the next day,
  so the object for the local time needs to be updated */
    if (timeHelper.isUsingNextDayTimes(loungeLocalTime, openLoungeTime)) {
      loungeLocalTime = loungeLocalTime.plus({ days: 1 });
    }

    if (loungeLocalTime < openLoungeTime) {
      return {
        isOpen: false,
        openingTime: timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: openLoungeTime,
          format,
          locale,
        }),
        closingTime: timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: closeLoungeTime,
          format,
          locale,
        }),
      };
    }

    if (
      timeHelper.isBetweenTimes(
        loungeLocalTime,
        openLoungeTime,
        closeLoungeTime
      )
    ) {
      return {
        isOpen: true,
        openingTime: timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: openLoungeTime,
          format,
          locale,
        }),
        closingTime: timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: closeLoungeTime,
          format,
          locale,
        }),
      };
    }
  }

  const openLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
    loungeTimes?.[0]?.open,
    loungeTimeZone
  );
  const closeLoungeTime = timeHelper.parseLoungeLuxonFromTimeString(
    loungeTimes?.[0]?.close,
    loungeTimeZone
  );

  return {
    isOpen: false,
    openingTime: loungeTimes?.[0]?.open
      ? timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: openLoungeTime,
          format,
          locale,
        })
      : '',
    closingTime: loungeTimes?.[0]?.close
      ? timeHelper.formatLoungeTimeFromLuxonObj({
          loungeTimeLuxon: closeLoungeTime,
          format,
          locale,
        })
      : '',
  };
}

export function generateLoungeHoursAsString(locale, lounge, format) {
  const loungeTimeZone = lounge?.airport?.time_zone || '';
  const loungeTimes = getLoungeOpenTimesForToday(locale, lounge);

  const isValidLoungeTime =
    timeHelper.isValidTime(loungeTimes.open) &&
    timeHelper.isValidTime(loungeTimes.close);

  if (isValidLoungeTime) {
    const openTimeString = timeHelper
      .formatLoungeTimeFromTimeString({
        timeString: loungeTimes.open,
        loungeTimeZone,
        format,
        locale,
      })
      .toLowerCase();
    const closeTimeString = timeHelper
      .formatLoungeTimeFromTimeString({
        timeString: loungeTimes.close,
        loungeTimeZone,
        format,
        locale,
      })
      .toLowerCase();
    return `${openTimeString} - ${closeTimeString}`;
  }
  return loungeTimes.note;
}

export function isNoteInsteadOfOpenCloseTimes(openHoursMap) {
  try {
    const openTime = openHoursMap.monday[0].open;
    const closeTime = openHoursMap.monday[0].close;
    if (timeHelper.isValidTime(openTime) && timeHelper.isValidTime(closeTime)) {
      return false;
    }
    return true;
  } catch (err) {
    throw new Error(
      'Issue traversing lounge hours. Check if the lounge object passed has the correct format.'
    );
  }
}

export function generateLoungeUrl({
  locale,
  loungeId,
  loungeName,
  loungeTerminal,
}) {
  const tempClosed =
    localesWithTempClosedTranslation[locale] ?? 'temporarily closed';
  const translatedTempClosedRegEx = new RegExp(tempClosed, 'gi');
  const regexTempClosed = translatedTempClosedRegEx.test(loungeName)
    ? translatedTempClosedRegEx
    : /temporarily closed/gi;

  const name = loungeName
    .replace(regexTempClosed, '')
    .replace(/[^\da-z-]+/gi, '-')
    .replace(/-+$/gi, '');

  const terminal = loungeTerminal.replace(/[^\da-z-]+/gi, '-');

  return `${name}-${terminal}-${loungeId}`;
}

export function prioritiseAccessRules(accessRulesList, providers) {
  return accessRulesList.reduce((prioritisedAccessRules, accessRule) => {
    const providerId = accessRule.provider_id;
    const matchedProvider = providers.find(
      (provider) =>
        provider.provider_id &&
        providerId &&
        provider.provider_id.toUpperCase().includes(providerId.toUpperCase())
    );
    let tmpAccessRule = accessRule;
    if (matchedProvider) {
      tmpAccessRule = { ...tmpAccessRule, order: matchedProvider.order };
    } else {
      tmpAccessRule = { ...tmpAccessRule, order: 9 };
    }
    prioritisedAccessRules.push(tmpAccessRule);
    return prioritisedAccessRules.sort((a, b) => a.order - b.order);
  }, []);
}

export function getPrioritisedProviderForLounge(locale, lounge, providers) {
  if (lounge.access_rules?.length === 0) {
    return null;
  }
  const providerId = prioritiseAccessRules(lounge.access_rules, providers)[0]
    .provider_id;
  return providers.find(
    (provider) =>
      provider.provider_id &&
      providerId &&
      provider.provider_id.toUpperCase().includes(providerId.toUpperCase())
  );
}

export function prioritiseLounges(locale, loungeList, providers) {
  return loungeList.reduce((prioritisedLounges, lounge) => {
    const matchedProvider = getPrioritisedProviderForLounge(
      locale,
      lounge,
      providers
    );
    if (matchedProvider) {
      const priorityLounge = lounge;
      priorityLounge.group = matchedProvider.order;
      prioritisedLounges.push(priorityLounge);
    } else {
      const nonPriorityLounge = lounge;
      nonPriorityLounge.group = 9;
      prioritisedLounges.push(nonPriorityLounge);
    }
    return prioritisedLounges.sort(
      (a, b) => a.group - b.group || a.name > b.name
    );
  }, []);
}

export const getCardPathname = ({
  airportCode,
  basePath,
  locale,
  lounge,
  product,
  pathname,
}) => {
  const productPath = `${basePath}/${product}`;
  const isCenturion = product.includes('centurion');

  if (
    !isCenturion &&
    locale === 'en-us' &&
    pathname?.includes('all-airports')
  ) {
    return `${productPath}/all-airports/`;
  }

  if (lounge) {
    const loungeTerminal = lounge.location?.terminal;
    const loungePath = generateLoungeUrl({
      locale,
      loungeName: lounge.name,
      loungeTerminal,
      loungeId: lounge.id,
    });
    return `${productPath}/${airportCode}/${loungePath}/`;
  }

  if (airportCode) {
    return `${productPath}/${airportCode}/`;
  }

  return `${productPath}/`;
};
